/**
 * Wat de beheerder aan de server vraagt: /admin en /prompts.
 *
 * Omzetten naar 3F en een tekst schrijven duren lang (het model schrijft
 * duizend woorden); die breken pas af na tweeënhalf en drie minuten.
 */
import { verzoek } from './client';

const post = (pad, json, extra) => verzoek(pad, { method: 'POST', json, ...extra });
const put = (pad, json) => verzoek(pad, { method: 'PUT', json });
const weg = (pad) => verzoek(pad, { method: 'DELETE' });

export const beheerApi = {
  // teksten
  haalTeksten: () => verzoek('/admin/texts'),
  haalTekst: (tekstId) => verzoek(`/admin/texts/${tekstId}`),
  maakTekst: (tekst) => post('/admin/texts', tekst),
  bewaarTekst: (tekstId, tekst) => put(`/admin/texts/${tekstId}`, tekst),
  verwijderTekst: (tekstId) => weg(`/admin/texts/${tekstId}`),
  zetOmNaar3F: (tekst, teksttype) => post('/admin/texts/convert-to-standard', { text: tekst, text_type: teksttype }, { wachtMs: 150000 }),
  schrijfTekst: (titel, beschrijving) => post('/admin/texts/generate', { titel, beschrijving: beschrijving || null }, { wachtMs: 180000 }),
  // historische fictie (stap 17): de sjablonen voor het formulier, en een verhaal laten schrijven (kan minuten duren)
  haalHistorischeSjablonen: () => verzoek('/admin/texts/historische-fictie/sjablonen'),
  schrijfHistorischeFictie: (invoer) => post('/admin/texts/historische-fictie/schrijf', invoer, { wachtMs: 360000 }),
  // de geschiedenis van het verhaal laten nakijken (stap 17c): een tweede vraag aan het model, tot twee minuten
  kijkHistorischeFictieNa: (deelteksten, opzet) => post('/admin/texts/historische-fictie/nakijken', { deelteksten, opzet }, { wachtMs: 240000 }),
  // een dakpanset van een standaardtekst laten maken (stap 22): het model, tot een paar minuten
  schrijfDakpanset: (bronId) => post('/admin/texts/dakpan/schrijf', { bron_id: bronId }, { wachtMs: 240000 }),

  // niveauteksten (stap 18): elke tekst op elk niveau, gemaakt en goedgekeurd door de beheerder.
  // Maken gebeurt op de achtergrond; de server antwoordt meteen, en het scherm vraagt het overzicht steeds opnieuw op.
  haalNiveauteksten: () => verzoek('/admin/level-texts'),
  maakNiveauteksten: (tekstId, niveaus) => post(`/admin/level-texts/${tekstId}/generate`, { niveaus: niveaus || null }),
  haalNiveauversie: (tekstId, niveau, welke) => verzoek(
    `/admin/level-texts/${tekstId}/version?niveau=${encodeURIComponent(niveau)}${welke ? `&welke=${welke}` : ''}`),
  /** vanGoedgekeurd: de beheerder bewerkte de goedgekeurde versie; dan begint het concept daar (een ander concept gaat weg). */
  bewaarConcept: (tekstId, niveau, alineas, vanGoedgekeurd = false) => put(`/admin/level-texts/${tekstId}/concept`,
    { niveau, paragraphs: alineas.map(a => ({ id: a.id, inhoud: a.inhoud })), van_goedgekeurd: vanGoedgekeurd }),
  /** Zonder niveau: elk concept van deze tekst. */
  keurNiveautekstGoed: (tekstId, niveau) => post(`/admin/level-texts/${tekstId}/approve`, { niveau: niveau || null }),
  gooiConceptWeg: (tekstId, niveau) => post(`/admin/level-texts/${tekstId}/discard`, { niveau }),
  // stap 19: de werkwijze bij het maken (van twee kanten, stapsgewijs omlaag, los), en de kerngedachten per tekst
  haalWerkwijze: () => verzoek('/admin/level-texts/method'),
  kiesWerkwijze: (werkwijze) => put('/admin/level-texts/method', { werkwijze }),
  haalKerngedachten: (tekstId) => verzoek(`/admin/level-texts/${tekstId}/key-ideas`),
  /** lijst: [{ tekst, soort }], in de volgorde van de tekst; versie: die je bewerkte (null: er was nog geen lijst) */
  bewaarKerngedachten: (tekstId, lijst, versie) => put(`/admin/level-texts/${tekstId}/key-ideas`, { lijst, versie: versie ?? null }),
  haalKerngedachtenOpnieuw: (tekstId) => post(`/admin/level-texts/${tekstId}/key-ideas/fetch`, undefined, { wachtMs: 120000 }),

  // de lengte per niveau (tabblad Tekstlengte); woorden: { 'M4': 300, ... }, alleen wat veranderde
  haalTekstlengtes: () => verzoek('/admin/text-lengths'),
  bewaarTekstlengtes: (woorden) => put('/admin/text-lengths', { woorden }),

  // illustraties
  haalIllustraties: () => verzoek('/admin/illustrations'),
  maakIllustratie: (tekstId, stijl, aanwijzing) => post('/admin/illustrations/generate', { text_id: tekstId, style: stijl, feedback: aanwijzing || null }),
  maakAlleIllustraties: (tekstId) => post('/admin/illustrations/generate-all', { text_id: tekstId }),
  verwijderIllustratie: (illustratieId) => weg(`/admin/illustrations/${illustratieId}`),
  /** Het plaatje als data-URL, zodat <img> het zonder token kan tonen. */
  haalIllustratieBeeld: (illustratieId) => verzoek(`/admin/illustrations/${illustratieId}/image-data`),

  // leerkrachten
  haalLeerkrachten: () => verzoek('/admin/teachers'),
  maakLeerkracht: (leerkracht) => post('/admin/teachers', leerkracht),
  verwijderLeerkracht: (leerkrachtId) => weg(`/admin/teachers/${leerkrachtId}`),

  // prompts
  haalPromptCategorieen: () => verzoek('/prompts/categories'),
  haalPrompts: () => verzoek('/prompts'),
  bewaarPrompt: (promptId, velden) => put(`/prompts/${promptId}`, velden),
  zetPromptTerug: (promptId) => post(`/prompts/reset/${promptId}`),

  // het taalmodel (tabblad LLM Config)
  haalTaalmodel: () => verzoek('/admin/llm-config'),
  bewaarTaalmodel: (instellingen) => put('/admin/llm-config', instellingen),

  // eigen voorleesstemmen (ook in LLM Config); een opname gaat als formulier mee, niet als JSON
  // taal: de taal van de opname; een andere dan Nederlands wordt de stem voor de vertaling in die taal
  maakStem: (naam, toestemming, opname, taal = 'Nederlands') => {
    const formulier = new FormData();
    formulier.append('naam', naam);
    formulier.append('toestemming', toestemming ? 'true' : 'false');
    formulier.append('taal', taal);
    formulier.append('opname', opname);
    return verzoek('/admin/voices', { method: 'POST', body: formulier, wachtMs: 120000 });
  },
  verwijderStem: (stemId) => weg(`/admin/voices/${encodeURIComponent(stemId)}`),

  // de spraakproef (stap 28): een zin met een gat inspreken; de opname (WAV) gaat als formulier mee
  haalSpraakproef: () => verzoek('/admin/speech-trial'),
  spreekIn: ({ opname, woord, zin, goedeVormen, bedoeld, model, spreker }) => {
    const formulier = new FormData();
    formulier.append('opname', opname, 'opname.wav');
    formulier.append('woord', woord);
    formulier.append('zin', zin);
    formulier.append('goede_vormen', goedeVormen);
    formulier.append('bedoeld', bedoeld);
    formulier.append('model', model);
    formulier.append('spreker', spreker || '');
    return verzoek('/admin/speech-trial', { method: 'POST', body: formulier, wachtMs: 90000 });
  },
  wisSpraakproef: () => weg('/admin/speech-trial'),
  /** De vaste stemmen die Mistral nu heeft: { stemmen: [{ id, naam, talen }] }. */
  haalMistralStemmen: () => verzoek('/admin/voices/mistral'),
  /** Een zin in een stem, als audio (blob). */
  // model weggelaten: het gekozen voorleesmodel; taal: een moedertaal, zoals bij een vertaling (dan Voxtral 3)
  proefluister: (stem, tekst, tempo = 'gewoon', model, taal) => verzoek('/admin/voices/preview', {
    method: 'POST', json: { voice: stem, text: tekst, tempo, ...(model ? { model } : {}), ...(taal ? { taal } : {}) }, als: 'blob',
  }),
};
